import { Reviewlink } from '@cube3/common/model/resource-types';
import { expiredCheck } from '@cube3/common/utils/expiredCheck';
import { useFeatures } from '@cube3/state/src/redux/components/Hooks/useFeatures';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { Feature, ResourceIdentifier } from '@cube3/state/types';
import { ReviewLinkUIProps } from '@cube3/ui/src/Prefabs/Reviewlink/Modals/ReviewLinkOverviewModalUI';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useModalActions } from '../../Modals/modalActions';
import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';

const requiredFeatures = {
  features: ['/WORKSPACE/SHARING/REVIEWS'] as Feature[]
};

/** return both legacy review-links and new review-links (if has the feature) */
export const useReviewsList = (
  identifier: ResourceIdentifier,
  pageSize?: number
) => {
  const [hasReviewsFeature] = useFeatures(requiredFeatures);

  const { openModal } = useModalActions();

  const [accountId] = useCurrentAccount(true);

  const retrievedReviews = useResourceList__ALPHA(
    useMemo(
      () => ({
        resourceType: identifier?.type,
        resourceId: identifier?.id,
        edgeType: 'share',
        edgeLabel: 'shares',
        params: {
          filter: {
            owner: accountId,
            intent: 'review'
          },
          page: {
            size: pageSize ? pageSize : -1
          }
        }
      }),
      [identifier, pageSize, accountId]
    )
  );

  const retrievedLegacyReviews = useResourceList__ALPHA(
    useMemo(
      () => ({
        resourceType: identifier?.type,
        resourceId: identifier?.id,
        edgeType: 'share',
        edgeLabel: 'shares',
        params: {
          filter: {
            account: accountId,
            intent: 'review-legacy'
          },
          page: {
            size: pageSize ? pageSize : -1
          }
        }
      }),
      [identifier, pageSize, accountId]
    )
  );

  const getReviewLinksUI = useCallback(
    (
      reviewlinks: Reviewlink[],
      intent: 'review' | 'review-legacy'
    ): ReviewLinkUIProps[] => {
      const newinfo: ReviewLinkUIProps[] = reviewlinks.map((item) => {
        const expiryTime = moment(item.expires_at).endOf('day');

        const uiInfo: ReviewLinkUIProps = {
          id: item.id,
          created_at: item.created_at,
          email_addresses: item.email_addresses,
          users: item.users,
          display_name: item.display_name,
          expired: expiredCheck(item.expires_at),
          expires: item.expires,
          expires_at: expiryTime.format(),
          node: item.relationships.node.id,
          onClick: () =>
            openModal('ShareSettings', {
              shareId: item.id,
              intent
            })
        };
        return uiInfo;
      });

      return newinfo;
    },
    [openModal, expiredCheck]
  );

  const info: ReviewLinkUIProps[] = useMemo(() => {
    const legacyReviews = getReviewLinksUI(
      retrievedLegacyReviews.resources,
      'review-legacy'
    );
    const reviews = getReviewLinksUI(retrievedReviews.resources, 'review');
    return hasReviewsFeature
      ? [].concat(legacyReviews, reviews)
      : legacyReviews;
  }, [
    retrievedLegacyReviews.resources,
    getReviewLinksUI,
    hasReviewsFeature,
    retrievedReviews.resources
  ]);

  return {
    reviewLinks: info,
    itemsCount:
      retrievedReviews.pageCounts?.itemCount +
      retrievedLegacyReviews.pageCounts?.itemCount,
    finishLoading:
      retrievedLegacyReviews.status === statuses.SUCCESS &&
      (hasReviewsFeature ? retrievedReviews.status === statuses.SUCCESS : true),
    loading:
      (retrievedLegacyReviews.status &&
        retrievedLegacyReviews.status !== statuses.SUCCESS &&
        retrievedLegacyReviews.status !== statuses.FAILED) ||
      (hasReviewsFeature &&
        retrievedReviews.status &&
        retrievedReviews.status !== statuses.SUCCESS &&
        retrievedReviews.status !== statuses.FAILED)
  };
};
